import React, { useState, useMemo } from 'react';
import {
    Box,
    TextField,
    InputAdornment,
    List,
    ListItemButton,
    ListItem,
    ListItemText,
    ListItemIcon,
    Checkbox,
    FormControlLabel,
    Typography,
    CircularProgress
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

export const UserSelectionStep = ({
    availableUsers,
    loadingAvailableUsers,
    selectedUsers,
    onSelectedUsersChange,
    onUserPermissionsChange
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Фильтрация пользователей для добавления
    const filteredAvailableUsers = useMemo(() => {
        if (!searchTerm.trim()) return availableUsers;
        const term = searchTerm.toLowerCase().trim();
        return availableUsers.filter(user =>
            (user.fullName && user.fullName.toLowerCase().includes(term)) ||
            (user.login && user.login.toLowerCase().includes(term))
        );
    }, [availableUsers, searchTerm]);

    // Логика выбора всех пользователей
    const allFilteredSelected = filteredAvailableUsers.length > 0 &&
        filteredAvailableUsers.every(user => selectedUsers.includes(user.guid));

    const someFilteredSelected = filteredAvailableUsers.length > 0 &&
        filteredAvailableUsers.some(user => selectedUsers.includes(user.guid)) &&
        !allFilteredSelected;

    const handleUserSelection = (userGuid) => {
        const newSelected = selectedUsers.includes(userGuid)
            ? selectedUsers.filter(id => id !== userGuid)
            : [...selectedUsers, userGuid];

        onSelectedUsersChange(newSelected);

        // Обновляем permissions
        if (!selectedUsers.includes(userGuid)) {
            onUserPermissionsChange(prevPerms => ({
                ...prevPerms,
                [userGuid]: {
                    role: 'Viewer',
                    advancedMode: false,
                    customCanManageTeam: false,
                    filePermissions: {}
                }
            }));
        } else {
            onUserPermissionsChange(prevPerms => {
                const newPerms = { ...prevPerms };
                delete newPerms[userGuid];
                return newPerms;
            });
        }
    };

    const handleSelectAll = () => {
        const filteredUserGuids = filteredAvailableUsers.map(user => user.guid || user.Guid);

        if (allFilteredSelected) {
            // Снимаем выделение
            const newSelected = selectedUsers.filter(id => !filteredUserGuids.includes(id));
            onSelectedUsersChange(newSelected);

            onUserPermissionsChange(prevPerms => {
                const newPerms = { ...prevPerms };
                filteredUserGuids.forEach(id => delete newPerms[id]);
                return newPerms;
            });
        } else {
            // Выделяем всех
            const newSelected = [...new Set([...selectedUsers, ...filteredUserGuids])];
            onSelectedUsersChange(newSelected);

            onUserPermissionsChange(prevPerms => {
                const newPerms = { ...prevPerms };
                filteredUserGuids.forEach(userGuid => {
                    if (!newPerms[userGuid]) {
                        newPerms[userGuid] = {
                            role: 'Viewer',
                            customCanManageTeam: false,
                            filePermissions: {}
                        };
                    }
                });
                return newPerms;
            });
        }
    };

    return (
        <Box>
            <TextField
                fullWidth
                variant="standard"
                placeholder="Поиск пользователей..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ marginBottom: '16px' }}
                className="search-input"
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon className="search-icon" />
                        </InputAdornment>
                    ),
                }}
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={allFilteredSelected}
                        indeterminate={someFilteredSelected}
                        onChange={handleSelectAll}
                        disabled={loadingAvailableUsers || filteredAvailableUsers.length === 0}
                    />
                }
                label={(searchTerm.trim() ? `Выбрать всех отфильтрованных` : 'Выбрать всех')
                    + (selectedUsers.length > 0 ? ` (${selectedUsers.length}/${availableUsers.length})` : '')}
            />

            <List style={{ maxHeight: 300, overflow: 'auto' }}>
                {loadingAvailableUsers ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={100}>
                        <CircularProgress size={30} />
                        <Typography variant="body2" style={{ marginLeft: 10 }}>
                            Загрузка пользователей...
                        </Typography>
                    </Box>
                ) : filteredAvailableUsers.length > 0 ? (
                    filteredAvailableUsers.map((user) => {
                        const userGuid = user.guid;
                        return (
                            <ListItemButton
                                key={userGuid}
                                dense
                                onClick={() => handleUserSelection(userGuid)}
                            >
                                <ListItemIcon>
                                    <Checkbox
                                        edge="start"
                                        checked={selectedUsers.includes(userGuid)}
                                        tabIndex={-1}
                                        disableRipple
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={user.fullName || user.login}
                                    secondary={user.login}
                                />
                            </ListItemButton>
                        );
                    })
                ) : (
                    <ListItem>
                        <ListItemText
                            primary="Пользователи не найдены"
                            secondary={searchTerm ? `По запросу "${searchTerm}" ничего не найдено` : 'Нет доступных пользователей'}
                        />
                    </ListItem>
                )}
            </List>
        </Box>
    );
};